import Footer from '@/pagecomponents/newcomponents/Footer'
import Navbar from '@/pagecomponents/newcomponents/Navbar'
import { atsPages } from '@/src/constants'
import Head from 'next/head'
import Link from 'next/link'
import styles from './ATSPlatformPage.module.css'
import ContentReviewNote from '@/components/ContentReviewNote'
import { lifeShackProductFacts } from '@src/config/productFacts'
import SmartRecruitersGuide from '@/components/SmartRecruitersGuide'

export default function ATSPlatformPage({ platform, atsData, smartRecruitersPublished = false }) {
  if (platform === 'smartrecruiters') return <SmartRecruitersGuide published={smartRecruitersPublished} />
  const signupHref = `/signup/?utm_source=seo&utm_medium=organic&utm_campaign=ats_guides&utm_content=${platform}`
  const pageTitle = `AI Auto Apply for ${atsData.name} Jobs | LifeShack`
  const pageDescription = atsData.pageDescription || `Learn how LifeShack supports ${atsData.name} application workflows, where portal variations can require review, and how to start with 5 free application credits.`
  const canonicalUrl = `https://www.lifeshack.com/job-board/${platform}/`
  const socialImage = 'https://www.lifeshack.com/widget.png'
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${lifeShackProductFacts.siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${atsData.name} auto apply guide`,
        item: canonicalUrl,
      },
    ],
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={socialImage} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
      </Head>
      <Navbar />
      <ContentReviewNote />
      <div className={styles.page}>
        {/* Main Container */}
        <div className={styles.mainContainer}>
          
          {/* Hero Section */}
          <section className={styles.hero}>
            <div className={styles.heroCopy}>
              <h1 className={styles.heroTitle}>
                {atsData.heroTitle || `Auto Apply ${atsData.name} Jobs with AI.`}
              </h1>
              <h2 className={styles.heroSubtitle}>
                {`Reduce repetitive application work.`}
              </h2>
              <p className={styles.heroText}>
                {atsData.directAnswer || `Tired of manually entering your entire work history and ${atsData.painPoint}? LifeShack can prepare and submit supported ${atsData.name} applications using the information in your profile.`}
              </p>
              <div className={styles.heroCta}>
                <a href="#start-free" className={styles.primaryCta}>
                  Automate my application
                </a>
              </div>
            </div>
            
            {/* AI Application Illustration */}
            <div className={styles.illustration}>
              <svg width="100%" viewBox="0 0 420 380" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width="420" height="380" rx="24" fill="#F8FAFC"/>
                <rect x="20" y="20" width="380" height="340" rx="16" fill="white"/>
                <rect x="35" y="35" width="350" height="310" rx="8" fill="#F1F5F9"/>
                
                <rect x="55" y="55" width="48" height="48" rx="24" fill="#E2E8F0"/>
                <rect x="115" y="65" width="180" height="12" rx="6" fill="#E2E8F0"/>
                <rect x="115" y="85" width="120" height="8" rx="4" fill="#E2E8F0"/>

                <rect x="55" y="125" width="80" height="8" rx="4" fill="#CBD5E1"/>
                <rect x="55" y="141" width="310" height="32" rx="8" fill="white"/>
                <rect x="65" y="149" width="150" height="16" rx="8" fill="#E2E8F0"/>

                <rect x="55" y="190" width="100" height="8" rx="4" fill="#CBD5E1"/>
                <rect x="55" y="206" width="310" height="32" rx="8" fill="white"/>
                <rect x="65" y="214" width="220" height="16" rx="8" fill="#E2E8F0"/>
                
                <rect x="55" y="255" width="90" height="8" rx="4" fill="#CBD5E1"/>
                <rect x="55" y="271" width="310" height="50" rx="8" fill="white"/>

                {/* AI Magic Star */}
                <g className={styles.pulse}>
                  <path d="M258.236 155.216L265.348 134.652L285.912 141.764L265.348 148.876L258.236 169.44L251.124 148.876L230.56 141.764L251.124 134.652L258.236 155.216Z" fill="#674BEC"/>
                  <path d="M261.348 147.876L258.236 155.216V140.536L261.348 147.876Z" fill="#A78BFA"/>
                  <path d="M258.236 140.536L251.124 134.652L255.793 141.191L258.236 140.536Z" fill="#C4B5FD"/>
                </g>
              </svg>
            </div>
          </section>

        </div>

        {/* Problem / Agitation Section */}
        <section className={styles.softSection}>
          <div className={`${styles.sectionInner} ${styles.center}`}>
            <h2 className={styles.sectionHeading}>{`The Challenge of the ${atsData.name} Portal`}</h2>
            <p className={styles.bodyText}>
              {`You found the perfect job, clicked 'Apply,' and landed on a ${atsData.name} portal. You know what comes next: ${atsData.challenge} It's a time-consuming task that can lead to application fatigue, causing you to miss out on great opportunities.`}
            </p>
            <p className={styles.bodyText}>
              {`LifeShack is designed to navigate supported workflows on platforms like ${atsData.name}, use the information in your profile, and handle repetitive fields. Employer-specific questions or portal changes may still require additional input.`}
            </p>
            {atsData.intentClarifier && (
              <p className={styles.bodyText}>{atsData.intentClarifier}</p>
            )}
          </div>
        </section>

        {/* How It Works Section */}
        <section className={styles.softSection}>
          <div className={styles.sectionInner}>
            <h2 className={`${styles.sectionHeading} ${styles.center}`}>
              {atsData.workflowHeading || 'Automate Your Application Process in 3 Simple Steps'}
            </h2>
            <div className={styles.featureGrid}>
              <div className={styles.featureItem}>
                <div className={styles.iconCircle}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className={styles.featureTitle}>{atsData.workflowSteps?.[0]?.title || 'Create Your Profile'}</h3>
                <p className={styles.featureText}>{atsData.workflowSteps?.[0]?.description || `Upload your resume once. We'll use this as the single source of truth for all applications.`}</p>
              </div>
              <div className={styles.featureItem}>
                <div className={styles.iconCircle}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className={styles.featureTitle}>{atsData.workflowSteps?.[1]?.title || 'Choose Your Desired Job Titles'}</h3>
                <p className={styles.featureText}>{atsData.workflowSteps?.[1]?.description || `LifeShack searches for matching roles and prepares supported ${atsData.name} application workflows using your saved preferences.`}</p>
              </div>
              <div className={styles.featureItem}>
                <div className={styles.iconCircle}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className={styles.featureTitle}>{atsData.workflowSteps?.[2]?.title || 'Get Batch Notifications'}</h3>
                <p className={styles.featureText}>{atsData.workflowSteps?.[2]?.description || `Review submitted applications and other workflow activity from your LifeShack dashboard.`}</p>
              </div>
            </div>
            {atsData.directAnswer && (
              <p className={`${styles.bodyText} ${styles.center}`}>
                Learn how the broader{' '}
                <Link className={styles.inlineLink} href="/ai-auto-apply/">
                  AI auto-apply workflow
                </Link>{' '}
                finds matching jobs and completes supported applications.
              </p>
            )}
          </div>
        </section>

        {/* CTA Button Section */}
        <section className={styles.ctaSection}>
          <a href="#start-free" className={styles.primaryCta}>
            Start with free application credits
          </a>
        </section>

        {/* Benefits Section */}
        <section className={`${styles.softSection} ${styles.benefitsSection}`}>
          <div className={styles.sectionInner}>
            <h2 className={`${styles.sectionHeading} ${styles.purpleHeading} ${styles.center}`}>The End of Repetitive Applications</h2>
            <div className={styles.benefitGrid}>
              <div className={styles.benefitCard}>
                <svg xmlns="http://www.w3.org/2000/svg" className={styles.benefitIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className={styles.featureTitle}>Reduce Data Entry</h3>
                <p className={styles.featureText}>Application length varies by employer. Reusing accurate profile information can reduce repetitive form entry on supported workflows.</p>
              </div>
              <div className={styles.benefitCard}>
                <svg xmlns="http://www.w3.org/2000/svg" className={styles.benefitIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className={styles.featureTitle}>Apply More</h3>
                <p className={styles.featureText}>Use automation to support a consistent search while continuing to review relevance, application activity, and employer responses.</p>
              </div>
              <div className={styles.benefitCard}>
                <svg xmlns="http://www.w3.org/2000/svg" className={styles.benefitIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className={styles.featureTitle}>Keep Information Consistent</h3>
                <p className={styles.featureText}>{`LifeShack uses the profile information you provide. Keep it current and review application activity for accuracy.`}</p>
              </div>
            </div>
          </div>
        </section>

        {atsData.details ? (
          <>
            <section className={styles.softSection}>
              <div className={styles.sectionInner}>
                <h2 className={`${styles.sectionHeading} ${styles.center}`}>
                  What an ApplyToJob application usually asks for
                </h2>
                {atsData.details.overview.map((paragraph) => (
                  <p className={styles.bodyText} key={paragraph}>
                    {paragraph}
                  </p>
                ))}
                <div className={styles.benefitGrid}>
                  {atsData.details.fieldGroups.map((group) => (
                    <div className={styles.detailCard} key={group.heading}>
                      <h3 className={styles.featureTitle}>{group.heading}</h3>
                      <ul className={styles.detailList}>
                        {group.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className={styles.softSection}>
              <div className={styles.sectionInner}>
                <h2 className={`${styles.sectionHeading} ${styles.center}`}>
                  A practical ApplyToJob automation workflow
                </h2>
                <p className={styles.bodyText}>
                  The safest workflow separates repeatable facts from answers
                  that depend on the specific employer and role.
                </p>
                <div className={styles.featureGrid}>
                  {atsData.details.workflow.map((step, index) => (
                    <div className={styles.detailCard} key={step.heading}>
                      <h3 className={styles.featureTitle}>
                        {index + 1}. {step.heading}
                      </h3>
                      <p className={styles.featureText}>{step.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className={styles.softSection}>
              <div className={styles.narrowInner}>
                <h2 className={`${styles.sectionHeading} ${styles.center}`}>
                  Where candidate review still matters
                </h2>
                {atsData.details.limitations.map((paragraph) => (
                  <p className={styles.bodyText} key={paragraph}>
                    {paragraph}
                  </p>
                ))}
                <h3 className={styles.detailSubheading}>ApplyToJob example</h3>
                <p className={styles.bodyText}>{atsData.details.example}</p>
                <h3 className={styles.detailSubheading}>
                  Official ApplyToJob and JazzHR sources
                </h3>
                <ul className={styles.sourceList}>
                  {atsData.details.sources.map((source) => (
                    <li key={source.href}>
                      <a
                        className={styles.sourceLink}
                        href={source.href}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                      >
                        {source.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </>
        ) : null}

        {['workday', 'greenhouse'].includes(platform) && process.env.SMARTRECRUITERS_GUIDE_ENABLED !== 'false' && (
          <section className={styles.softSection}>
            <div className={styles.narrowInner}>
              <h2 className={styles.sectionHeading}>Applying through another employer system?</h2>
              <p className={styles.bodyText}>
                Employers choose different forms and screening questions. See our{' '}
                <Link href="/job-board/smartrecruiters/"><a className={styles.sourceLink}>SmartRecruiters application guide</a></Link>{' '}
                if your job posting uses SmartRecruiters.
              </p>
            </div>
          </section>
        )}

        {/* Final CTA Section */}
        <section id="start-free" className={styles.finalSection}>
          <div className={styles.narrowInner}>
            <h2 className={styles.sectionHeading}>See whether LifeShack fits your {atsData.name} workflow.</h2>
            <p className={styles.bodyText}>
              {lifeShackProductFacts.introductoryOffer.description}
            </p>
            <div className={styles.finalCta}>
              <a href={signupHref} className={styles.primaryCta}>
                Automate my next application
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className={styles.faqSection}>
          <div className={styles.narrowInner}>
            <h2 className={`${styles.sectionHeading} ${styles.center}`}>{`FAQs about Auto-Applying to ${atsData.name}`}</h2>
            <div className={styles.faqList}>
              {atsData.intentFaqs?.map((faq) => (
                <div key={faq.question}>
                  <h3 className={styles.faqQuestion}>{faq.question}</h3>
                  <p className={styles.faqAnswer}>{faq.answer}</p>
                </div>
              ))}
              <div>
                <h3 className={styles.faqQuestion}>{`Is it safe to use LifeShack with ${atsData.name}?`}</h3>
                <p className={styles.faqAnswer}>{`LifeShack uses the profile information you provide to prepare supported applications. Keep your information accurate, review activity in the dashboard, and consult the privacy policy for current data-handling details.`}</p>
              </div>
              <div>
                <h3 className={styles.faqQuestion}>{`Will companies know I used an AI tool to apply?`}</h3>
                <p className={styles.faqAnswer}>{`Employers receive the information submitted in the application. LifeShack does not promise that an employer or platform cannot identify automated activity, so use the product in line with applicable terms and your own preferences.`}</p>
              </div>
              <div>
                <h3 className={styles.faqQuestion}>{`Does this work for every company that uses ${atsData.name}?`}</h3>
                <p className={styles.faqAnswer}>{`No. Employers can customize ${atsData.name}, and portals change over time. Unsupported fields, additional questions, or technical changes can prevent an application from completing.`}</p>
              </div>
              {atsData.details?.faqs.map((faq) => (
                <div key={faq.question}>
                  <h3 className={styles.faqQuestion}>{faq.question}</h3>
                  <p className={styles.faqAnswer}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.compareSection}>
          <div className={styles.narrowInner}>
            <h2 className={styles.sectionHeading}>
              Compare application automation tools.
            </h2>
            <p className={styles.bodyText}>
              See how cloud application agents differ from browser extensions,
              trackers, bots, and managed services.
            </p>
            <div className={styles.compareLinks}>
              <Link className={styles.compareLink} href="/best-ai-auto-apply-tools/">
                Best AI auto-apply tools
              </Link>
              <Link className={styles.compareLink} href="/compare/">
                Compare LifeShack
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  )
}

ATSPlatformPage.isPublicSeoPage = true
ATSPlatformPage.preloadSeoHero = false

// Generate static paths for all ATS platforms
export async function getStaticPaths() {


  const previewPages = process.env.SMARTRECRUITERS_GUIDE_PREVIEW === 'true' ? ['smartrecruiters'] : []
  const paths = Array.from(new Set([...atsPages, ...previewPages])).map((platform) => ({
    params: { platform }
  }))

  return {
    paths,
    fallback: false // Change to 'blocking' if you want on-demand generation
  }
}

// Generate props for each platform
export async function getStaticProps({ params }) {
  const { platform } = params
  if (platform === 'smartrecruiters') {
    const published = process.env.SMARTRECRUITERS_GUIDE_ENABLED !== 'false'
    if (!published && process.env.SMARTRECRUITERS_GUIDE_PREVIEW !== 'true') return { notFound: true }
    return { props: { platform, atsData: { name: 'SmartRecruiters' }, smartRecruitersPublished: published } }
  }

  // ATS-specific data and messaging
const atsData = {
    successfactors: {
        name: 'SAP SuccessFactors',
        heroTitle: 'Apply to SAP SuccessFactors Jobs with LifeShack',
        pageDescription: 'Learn how SAP SuccessFactors application portals vary by employer, what information to prepare, and how LifeShack handles supported application forms.',
        directAnswer: 'LifeShack can complete supported SAP SuccessFactors application forms using your saved profile and resume. Each employer may configure its own account, questions, privacy consent, and upload requirements, so some applications may need your input.',
        painPoint: 'creating accounts and re-entering profile details across employer career portals',
        challenge: 'SuccessFactors career sites can look different for each employer. Some jobs start on a branded company page before opening a SAP application form, and account registration or custom questions may add steps.',
        workflowHeading: 'How LifeShack handles supported SuccessFactors applications',
        workflowSteps: [
          {title: 'Keep your profile accurate', description: 'Add current contact details, work history, location, authorization, and a resume that reflects your experience.'},
          {title: 'Set your job preferences', description: 'Choose roles and locations you want. LifeShack uses those preferences before attempting supported employer forms.'},
          {title: 'Check application activity', description: 'A filled form is not a confirmed submission. Check your application history for the result, and update missing profile details when needed.'},
        ],
        intentFaqs: [
          {question: 'Can LifeShack apply to every SuccessFactors job?', answer: 'No. Employers can add screening questions, account verification, assessments, or other steps that prevent automatic completion. LifeShack only reports a successful application after it detects a submission confirmation.'},
          {question: 'Will an employer receive my resume?', answer: 'For a supported application, LifeShack uploads the resume selected by your preferences. Employer forms may require other documents or information before submission.'},
        ],
    },
    workday: {
        name: 'Workday',
        heroTitle: 'Automate Workday Job Applications with AI',
        pageDescription: 'See how LifeShack automates supported Workday applications, handles recurring candidate fields, and flags steps that need review. Start with 5 free credits.',
        directAnswer: 'LifeShack can automate supported Workday job applications by reusing accurate profile details, preparing recurring fields, and tracking completed application activity. Because each employer can configure its portal differently, custom questions or changed workflows may still need your input.',
        painPoint: 're-entering details and answering employer-specific questions across differently configured portals',
        challenge: 'Workday application flows can vary by employer. Recurring candidate fields, account steps, and custom questions can make applications repetitive, while changed or unsupported fields may still require attention.',
        intentClarifier: 'This guide covers candidate-side Workday job application automation. It is not about software employers use to automate hiring, recruiting, or Workday administration.',
        workflowHeading: 'How Workday Auto Apply Works for Job Seekers',
        workflowSteps: [
          {
            title: 'Keep Your Candidate Profile Current',
            description: 'Add accurate contact details, work history, education, and a current resume so supported fields can be completed consistently.',
          },
          {
            title: 'Set Your Job Preferences',
            description: 'Choose the titles, locations, and other preferences LifeShack should use to find relevant roles and prepare supported Workday applications.',
          },
          {
            title: 'Review Application Activity',
            description: 'Track completed applications in your dashboard and step in when an employer adds a custom question or unsupported field.',
          },
        ],
        intentFaqs: [
          {
            question: 'Can AI auto apply to Workday jobs?',
            answer: 'Yes, for supported application workflows. LifeShack can use your saved profile to complete recurring candidate fields and submit supported Workday applications, but employer-specific questions or portal changes can still require input.',
          },
          {
            question: 'Is this Workday hiring automation for employers?',
            answer: 'No. This guide is for job seekers automating their own applications with LifeShack. It does not describe employer-side recruiting automation or claim that Workday or a specific employer uses AI to evaluate applicants.',
          },
        ],
    },
    icims: {
        name: 'iCIMS',
        painPoint: 'manually re-entering your entire work history after uploading your resume',
        challenge: 'The core frustration with iCIMS is its frequent failure to parse resume data correctly. This forces you to manually re-type every job title, company, and date from your career history, making each application a lengthy data-entry task.',
    },
    lever: {
        name: 'Lever',
        painPoint: 'answering a new set of custom, open-ended questions for every role',
        challenge: 'Lever is generally user-friendly, but its strength for recruiters is a challenge for applicants: custom \'knockout\' questions. Answering these thoughtfully for each application is time-consuming and mentally draining.',
    },
    greenhouse: {
        name: 'Greenhouse',
        heroTitle: 'Automate Greenhouse Job Applications with AI',
        pageDescription: 'See how LifeShack automates supported Greenhouse applications, handles resume and recurring fields, and flags steps that need review. Start with 5 free credits.',
        directAnswer: 'LifeShack can automate supported Greenhouse job applications by using your current resume and profile details, preparing recurring candidate fields, and tracking completed application activity. Employer-added questions or changed workflows may still need your input.',
        painPoint: 'uploading resumes and answering employer-specific questions for each application',
        challenge: 'Greenhouse application flows can vary by employer. Resume uploads, recurring candidate details, and custom questions can still make a high-volume search repetitive, while changed or unsupported fields may require attention.',
        intentClarifier: 'This guide covers candidate-side Greenhouse job application automation. It is not about automations employers configure inside Greenhouse for recruiting or hiring.',
        workflowHeading: 'How Greenhouse Auto Apply Works for Job Seekers',
        workflowSteps: [
          {
            title: 'Use a Current Resume and Profile',
            description: 'Keep your resume, contact details, work history, and reusable answers accurate before LifeShack prepares supported Greenhouse applications.',
          },
          {
            title: 'Choose Relevant Job Preferences',
            description: 'Set titles, locations, and other preferences so LifeShack can find matching roles and prepare the supported candidate workflow.',
          },
          {
            title: 'Review Application Activity',
            description: 'Track completed applications in your dashboard and step in when an employer adds a custom question or unsupported field.',
          },
        ],
        intentFaqs: [
          {
            question: 'Can AI auto apply to Greenhouse jobs?',
            answer: 'Yes, for supported application workflows. LifeShack can use your saved resume and profile information to complete recurring candidate fields and submit supported Greenhouse applications, but employer-specific questions or portal changes can still require input.',
          },
          {
            question: 'Is this Greenhouse automation for employers?',
            answer: 'No. This guide is for job seekers automating their own applications with LifeShack. It does not cover recruiting automations configured by employers inside Greenhouse.',
          },
        ],
    },
    bamboohr: {
        name: 'BambooHR',
        painPoint: 'applying through a system that feels more like an HR tool than an application portal',
        challenge: 'Because BambooHR is primarily an all-in-one HR platform, its application module can feel less intuitive than dedicated ATS platforms, with a clunkier interface and workflow.',
    },
    jobvite: {
        name: 'Jobvite',
        painPoint: 'a dated interface and long lists of pre-screening questions',
        challenge: 'The main challenge with Jobvite is its often clunky, older interface. It frequently requires answering a long list of mandatory pre-screening questions upfront before you can even submit your core application.',
    },
    workable: {
        name: 'Workable',
        painPoint: 'the endless custom \'application questions\' that slow you down',
        challenge: 'The core frustration with Workable is the near-universal requirement to answer several custom, non-standard questions for almost every role, turning a quick application into a lengthy questionnaire.',
    },
    breezy: {
        name: 'Breezy HR',
        painPoint: 'a confusing application pipeline that leaves you guessing your status',
        challenge: 'The challenge with BreezyHR is its multi-stage \'pipeline\' view. While great for recruiters, it can often be unclear to candidates about their actual application status, leading to uncertainty.',
    },
    comeet: {
        name: 'Comeet',
        painPoint: 'slow response times due to \'collaborative hiring\' processes',
        challenge: 'The challenge with Comeet is a side effect of its collaborative features. The platform makes it easy for multiple team members to weigh in, which can sometimes lead to slower decision-making and response times.',
    },
    recruitee: {
        name: 'Recruitee',
        painPoint: 'an inconsistent application experience from one company to the next',
        challenge: 'Because Recruitee is highly customizable for employers, the candidate experience can vary wildly. This inconsistency makes it difficult to get into a rhythm, as each application portal can have a completely different workflow.',
    },
    rippling: {
        name: 'Rippling',
        painPoint: 'applying through a system that seems to prioritize payroll data over your skills',
        challenge: 'As an HR and payroll-first platform, Rippling\'s application process can sometimes feel like you\'re filling out onboarding paperwork rather than showcasing your qualifications for a role.',
    },
    ukg: {
        name: 'UKG',
        painPoint: 'navigating complex, enterprise-level HR portals just to apply for one job',
        challenge: 'The challenge with UKG is its enterprise focus. The application workflow is often embedded within a larger, more complex HR suite, which can feel unintuitive and overly corporate for a simple job application.',
    },
    ashbyhq: {
        name: 'Ashby',
        painPoint: 'clunky portals and wondering if your application was even seen',
        challenge: 'The main challenge with Ashby is also its strength: it\'s so fast and clean that it\'s easy to send an application without double-checking your work. Its modern interface and clear communication are why it\'s a favorite among high-growth startups.',
    },
    applytojob: {
      name: 'ApplyToJob',
      painPoint: 're-entering profile details and answering employer-specific questions in a customized JazzHR application',
      challenge: 'ApplyToJob uses a recognizable JazzHR-hosted application shell, but employers control the required questions and optional sections. Contact and resume fields are often predictable; screening, consent, and compliance fields can vary by job.',
      details: {
        overview: [
          'ApplyToJob is the candidate-facing application domain used by employers on JazzHR. A typical form starts with contact details and a resume, then adds fields selected by the employer. That is why two jobs on ApplyToJob can look similar at first and still require different answers before submission.',
          'Current public application forms commonly ask for a candidate’s name, email, phone number, location, and resume. Depending on the employer, the same form may also ask for a cover letter, referral source, LinkedIn profile, portfolio or website, desired salary, availability, work authorization, certifications, references, or written responses. Treat those as examples rather than a promise that every application contains the same fields.',
        ],
        fieldGroups: [
          {
            heading: 'Reusable profile details',
            items: [
              'Name, email, phone number, and current location',
              'Resume attachment or pasted resume text',
              'Saved LinkedIn, portfolio, and professional website links',
            ],
          },
          {
            heading: 'Employer screening fields',
            items: [
              'Role-specific experience and free-response questions',
              'Certifications, compensation expectations, and start date',
              'Work authorization, residency, relocation, and travel answers',
            ],
          },
          {
            heading: 'Optional or sensitive sections',
            items: [
              'Referral information and text-message consent',
              'Voluntary EEO, veteran, and disability disclosures',
              'References, acknowledgements, and a human-verification check',
            ],
          },
        ],
        workflow: [
          {
            heading: 'Check the job requirements',
            body: 'Confirm the role, location, seniority, and any required license, certification, or work status before beginning the application.',
          },
          {
            heading: 'Reuse verified facts',
            body: 'Populate supported contact, location, resume, and profile-link fields from information the candidate has already supplied and kept current.',
          },
          {
            heading: 'Attach the right resume',
            body: 'Use the intended resume for this role and respect the file types and size limit shown on the live form. Representative ApplyToJob forms currently accept common document formats and enforce a file-size limit.',
          },
          {
            heading: 'Answer from candidate evidence',
            body: 'Use saved, truthful candidate information for screening questions. Do not invent years of experience, certifications, salary expectations, authorization, or examples of past work.',
          },
          {
            heading: 'Pause when review is required',
            body: 'Candidate input may be needed for unfamiliar custom questions, voluntary disclosures, consent choices, human checks, or any answer that cannot be supported by the profile.',
          },
          {
            heading: 'Submit and track the result',
            body: 'Complete supported applications, then record the submission so the candidate can review activity and follow up with the employer.',
          },
        ],
        limitations: [
          'JazzHR lets employers add questionnaires and custom fields, so there is no single universal ApplyToJob form. A workflow that succeeds for one company can encounter different required questions, acknowledgements, uploads, or consent choices at another. Portal changes and human-verification controls can also stop an otherwise supported application.',
          'LifeShack is designed to reduce repeat entry where the workflow is supported, not manufacture candidate facts or bypass a decision that belongs to the applicant. Some employers explicitly warn that generic or AI-generated answers that do not reflect the candidate’s own experience can disqualify an application. Accuracy matters more than forcing every form through automatically.',
        ],
        example: 'Imagine a candidate applying to an account executive role. Their name, contact details, location, resume, LinkedIn URL, and factual employment history can be reused. Questions about expected compensation, travel, a specific sales result, work authorization, or willingness to relocate still need answers grounded in that candidate’s circumstances. For a regulated healthcare role, a required license or certification must likewise be verified rather than inferred from a job title.',
        faqs: [
          {
            question: 'Is every ApplyToJob application form the same?',
            answer: 'No. ApplyToJob pages use a common JazzHR-hosted shell, but each employer can choose its own required fields, screening questions, uploads, acknowledgements, and optional compliance sections.',
          },
          {
            question: 'Which ApplyToJob fields can be reused?',
            answer: 'On supported workflows, reusable fields can include contact details, location, resume information, LinkedIn or portfolio links, and truthful answers already saved in the candidate profile. The live form determines what is actually required.',
          },
          {
            question: 'Which ApplyToJob questions need candidate review?',
            answer: 'Review is especially important for compensation, certifications, work authorization, relocation, free-response questions, text consent, voluntary demographic forms, references, and human-verification checks.',
          },
          {
            question: 'Can LifeShack guarantee every ApplyToJob submission?',
            answer: 'No. LifeShack supports repeatable application workflows, but employer customization, new required fields, portal changes, and human checks can prevent completion or require candidate input.',
          },
        ],
        sources: [
          {
            label: 'Representative live ApplyToJob application form',
            href: 'https://fusionauth.applytojob.com/apply/L15nmoft50/Account-Executive-Remote-EU-LondonBerlinAmsterdam-Metro',
          },
          {
            label: 'JazzHR screening and questionnaire documentation',
            href: 'https://www.jazzhr.com/onboarding/screening',
          },
          {
            label: 'JazzHR custom-fields documentation',
            href: 'https://www.jazzhr.com/blog/custom-fields',
          },
          {
            label: 'JazzHR data-processing terms and candidate-data categories',
            href: 'https://www.jazzhr.com/wp-content/uploads/2022/08/JazzHR-DPA-2022-version.pdf',
          },
        ],
      },
    }
}

  const platformData = atsData[platform]

  if (!platformData) {
    return {
      notFound: true
    }
  }

  return {
    props: {
      platform,
      atsData: platformData
    }
  }
}
